import { useCallback } from "react";
import { useReactFlow } from "reactflow";
import { useDeleteFarmGate } from "../../../../api/farmGate";
import { nodeType } from "../../../../config/drawingConstants";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import { SaveDocumentDataHandle } from "../types/cultivarForm.types";

interface UseCultivarDeleteOptions {
  farmGateId: string;
  nodeItem: any;
  childNodes: any[];
  notesRef: React.RefObject<SaveDocumentDataHandle>;
  onCloseDeleteModal: () => void;
  setNodeItem: (value: any) => void;
}

export const useCultivarDelete = ({
  farmGateId,
  nodeItem,
  childNodes,
  notesRef,
  onCloseDeleteModal,
  setNodeItem
}: UseCultivarDeleteOptions) => {
  const { deleteElements, getEdges } = useReactFlow();
  const { mutate: deleteFarmGate, isLoading: isDeleting } = useDeleteFarmGate();

  const removeFarmGateFromCanvas = useCallback(
    (farmGateNodeId: string) => {
      const connectedEdges = getEdges().filter(
        edge => edge.source === farmGateNodeId || edge.target === farmGateNodeId
      );

      deleteElements({ nodes: [{ id: farmGateNodeId }], edges: connectedEdges });
      onCloseDeleteModal();
      setNodeItem(null);
    },
    [deleteElements, getEdges, onCloseDeleteModal, setNodeItem]
  );

  const deleteFarmGateGroup = useCallback(() => {
    const farmGateNodeId = nodeItem?.parentNode;
    const hasNonCultivarChildren = childNodes.some(
      childNode => childNode?.data.icon !== nodeType.Cultivar
    );

    if (hasNonCultivarChildren) {
      onCloseDeleteModal();
      errorView("Please delete all items inside the farm gate except cultivar");
      return;
    }

    if (!farmGateNodeId) {
      onCloseDeleteModal();
      return;
    }

    if (farmGateId) {
      deleteFarmGate(farmGateId, {
        onSuccess: () => {
          notesRef.current?.deleteComment();
          removeFarmGateFromCanvas(farmGateNodeId);
          successMessage("Cultivation deleted successfully");
        },
        onError: () => {
          onCloseDeleteModal();
          errorView("Failed to delete cultivation");
        }
      });
      return;
    }

    removeFarmGateFromCanvas(farmGateNodeId);
    successMessage("Cultivation deleted successfully");
  }, [
    childNodes,
    deleteFarmGate,
    farmGateId,
    nodeItem?.parentNode,
    notesRef,
    onCloseDeleteModal,
    removeFarmGateFromCanvas
  ]);

  return {
    deleteFarmGateGroup,
    isDeleting
  };
};
